import React from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryButton from '../components/common/ButtonsV2/PrimaryButton';
import NotFoundImage from '../assets/images/NotFoundImage.svg';
import MainPageLayout from '../components/common/MainPageLayout';

/**
 * NotFound Page Component
 *
 * This component renders a custom 404 "Not Found" page. It is displayed when a user navigates to a route that doesn't exist.
 * The page includes a responsive illustration, a large "404" heading, a descriptive message, and a button to navigate back to the home page.
 *
 * Features:
 * - Uses React and React Router's useNavigate hook for navigation.
 * - Incorporates a responsive design with Tailwind CSS classes, ensuring the layout adapts to different screen sizes.
 * - Utilizes a shared MainPageLayout component for consistent page structure and header behavior.
 * - Displays an illustration image (NotFoundImage) that scales appropriately on various devices.
 *
 * @component
 * @returns {JSX.Element} The rendered NotFound page.
 */
const NotFound = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col lg:flex-row items-center md:gap-2 lg:gap-20 max-w-5xl mx-auto px-4 sm:px-8">
          <div className="w-full lg:max-w-md">
            <img src={NotFoundImage} alt="Not Found Illustration" className="w-full h-auto" />
          </div>

          <div className="flex flex-col items-center">
            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-custom-light-blue leading-none">
              404
            </h1>
            <h2 className="text-2xl sm:text-3xl font-semibold text-custom-light-blue mt-2">
              Page Not Found
            </h2>
            <p className="text-base sm:text-lg text-center font-medium text-black mt-4 w-[220px]">
              Don't worry, we'll help you get back on track!
            </p>
            <div className="mt-6 sm:mt-8">
              <PrimaryButton onClick={handleGoHome}>Go Home</PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </MainPageLayout>
  );
};

export default NotFound;
